import { ReactElement, useMemo } from 'react';
import { IContentTag } from 'core';
import styles from '../../pages/Activity/ActivityDetail/ActivityDetail.module.scss';

interface IProps {
  contentTags: IContentTag[];
}

export const ContentTags = ({ contentTags }: IProps): ReactElement | null => {
  const contentAreas = useMemo<string[]>(() => {
    if (!contentTags?.length) {
      return [];
    }

    const childNamesOf = (parentName: string): string[] =>
      [
        ...new Set(
          contentTags
            .filter((tag) => tag.parentName === parentName && !!tag.name)
            .map((tag) => (tag.name as string).trim()),
        ),
      ].sort((a, b) => a.localeCompare(b));

    const highLevelAreas = childNamesOf('CME');

    // For each high-level area, append its specific (child) content areas after a colon.
    const parts = highLevelAreas.map((areaName) => {
      const specificAreas = childNamesOf(areaName);
      return specificAreas.length ? `${areaName}: ${specificAreas.join(', ')}` : areaName;
    });

    // Fallback: when there are no 'CME' parents, group every tag by its own parentName
    // so non-CME parent/child pairs are still displayed.
    if (!parts.length) {
      const parents = [...new Set(contentTags.map((tag) => tag.parentName).filter(Boolean))].sort((a, b) =>
        (a as string).localeCompare(b as string),
      );

      parents.forEach((parent) => {
        const children = childNamesOf(parent as string);
        if (children.length) {
          parts.push(`${parent}: ${children.join(', ')}`);
        }
      });
    }

    return parts;
  }, [contentTags]);

  if (!contentAreas.length) {
    return null;
  }

  return (
    <div className={styles['detail-snippet']}>
      <div className={styles['caption-text']}>Content Tagging</div>

      <p className={styles['detail-content']}>
        Content Meets State Licensing Requirements for the following Content Area(s): {contentAreas.join('; ')}
      </p>
    </div>
  );
};
